import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Film, Loader2, Sparkles, RefreshCw, X, ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";
import RendyPresetsPreviewModal from "@/components/admin/RendyPresetsPreviewModal";

const API = getApiUrl();

const STATUS_LABEL = {
  generating: "Generating\u2026",
  exporting: "Packaging final video\u2026",
  ready: "Ready",
  failed: "Failed",
};

/**
 * RendyVideoPanel — "easy options" Rendy dashboard dropped into both the
 * agent and master-admin Edit Property modals, next to the image gallery.
 * One button to generate a video from this property's photos, auto-polls
 * until Rendy finishes and the export zip has been unpacked + saved to
 * permanent storage, lists every past generation with inline previews, and
 * (inside the modal) a "Preview Rendy Presets" action to browse/manage the
 * presets available for those generations.
 */
export default function RendyVideoPanel({ propertyId, isMasterAdmin = false, onAdded, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [togglingClip, setTogglingClip] = useState(null);

  const toggleClipInGallery = async (videoId, clipIndex, inGallery) => {
    const key = `${videoId}-${clipIndex}`;
    setTogglingClip(key);
    try {
      await axios.post(
        `${API}/rendy/properties/${propertyId}/videos/${videoId}/clips/${clipIndex}/gallery`,
        { in_gallery: inGallery },
        { withCredentials: true }
      );
      const v = videos.find((x) => x.video_id === videoId);
      const clip = v?.videos?.[clipIndex];
      setVideos((prev) =>
        prev.map((x) =>
          x.video_id === videoId
            ? { ...x, videos: x.videos.map((c, i) => (i === clipIndex ? { ...c, in_gallery: inGallery } : c)) }
            : x
        )
      );
      if (clip) {
        if (inGallery) onAdded && onAdded([clip]);
        else onRemoved && onRemoved([clip.url]);
      }
      toast.success(inGallery ? `Clip ${clipIndex + 1} added to the gallery` : `Clip ${clipIndex + 1} removed from the gallery`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update gallery");
    } finally {
      setTogglingClip(null);
    }
  };
  const [retryingId, setRetryingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const pollRef = useRef(null);

  const deleteVideo = async (videoId) => {
    if (!window.confirm("Delete this generation from the Rendy video library? This also removes it from the property gallery if it's there. This can't be undone.")) return;
    setDeletingId(videoId);
    try {
      const v = videos.find((x) => x.video_id === videoId);
      const urls = (v?.videos || []).map((c) => c.url).filter(Boolean);
      const { data } = await axios.delete(
        `${API}/rendy/properties/${propertyId}/videos/${videoId}`,
        { withCredentials: true }
      );
      setVideos((prev) => prev.filter((x) => x.video_id !== videoId));
      if (data?.removed_from_gallery > 0 && urls.length) onRemoved && onRemoved(urls);
      toast.success("Video deleted from the library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete video");
    } finally {
      setDeletingId(null);
    }
  };

  const retryDownload = async (videoId) => {
    setRetryingId(videoId);
    try {
      const { data } = await axios.post(
        `${API}/rendy/properties/${propertyId}/videos/${videoId}/retry-download`,
        {},
        { withCredentials: true }
      );
      setVideos((prev) => prev.map((v) => (v.video_id === videoId ? data : v)));
      toast.success("Re-requesting the export from Rendy \u2014 no re-render needed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to retry download");
    } finally {
      setRetryingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/rendy/properties/${propertyId}/videos`, { withCredentials: true });
      setVideos(data?.videos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Rendy videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-poll any generation still in progress while the dialog is open.
  // This re-fetches the lightweight, read-only videos list (no Rendy API
  // calls) rather than hitting /refresh — the actual Rendy communication,
  // export download, unzip and gallery-attach now happen exclusively in
  // the background schedulers (2-minute + 15-second pollers), which have
  // no request/proxy timeout to race against. Polling /refresh directly
  // from here used to risk a client/Cloudflare timeout on a large export.
  const pendingCount = videos.filter((v) => v.status === "generating" || v.status === "exporting").length;
  useEffect(() => {
    clearInterval(pollRef.current);
    if (open && pendingCount) {
      pollRef.current = setInterval(load, 6000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingCount]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/rendy/properties/${propertyId}/generate`, {}, { withCredentials: true });
      setVideos((prev) => [data, ...prev]);
      toast.success("Rendy is generating your video \u2014 this can take a couple of minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Rendy generation");
    } finally {
      setGenerating(false);
    }
  };

  const addToGallery = async (videoId) => {
    setAddingId(videoId);
    try {
      const { data } = await axios.post(
        `${API}/rendy/properties/${propertyId}/videos/${videoId}/add-to-gallery`,
        {},
        { withCredentials: true }
      );
      toast.success(data.added > 0 ? "Added to the property gallery" : "Already in the gallery");
      setVideos((prev) => prev.map((x) => (x.video_id === videoId ? { ...x, videos: (x.videos || []).map((c) => ({ ...c, in_gallery: true })) } : x)));
      if (data.added > 0) {
        const v = videos.find((x) => x.video_id === videoId);
        onAdded && onAdded(v?.videos || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add video to gallery");
    } finally {
      setAddingId(null);
    }
  };

  const removeFromGallery = async (videoId) => {
    setRemovingId(videoId);
    try {
      const { data } = await axios.post(
        `${API}/rendy/properties/${propertyId}/videos/${videoId}/remove-from-gallery`,
        {},
        { withCredentials: true }
      );
      toast.success(data.removed > 0 ? "Removed from the property gallery" : "Wasn't in the gallery");
      setVideos((prev) => prev.map((x) => (x.video_id === videoId ? { ...x, videos: (x.videos || []).map((c) => ({ ...c, in_gallery: false })) } : x)));
      if (data.removed > 0) {
        const v = videos.find((x) => x.video_id === videoId);
        const urls = (v?.videos || []).map((c) => c.url);
        onRemoved && onRemoved(urls);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove video from gallery");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className="rounded-none text-xs"
        data-testid="open-rendy-panel-btn"
      >
        <Film className="mr-1 h-3 w-3" />
        Rendy Video
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] sm:max-w-2xl rounded-none max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="rendy-video-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Film className="h-5 w-5 text-primary" /> Rendy Video Dashboard
            </DialogTitle>
            <DialogDescription>
              Turn this property's photos into an AI marketing video. Generation runs in the background — you can close this and come back.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={generate} disabled={generating} data-testid="rendy-generate-video-btn" className="rounded-none">
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate Rendy Video
              </Button>
              <RendyPresetsPreviewModal editable={isMasterAdmin} />
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading past generations…
              </div>
            ) : videos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4" data-testid="rendy-no-videos">
                No videos generated yet for this property.
              </p>
            ) : (
              <div className="space-y-3" data-testid="rendy-videos-list">
                {videos.map((v) => (
                  <div key={v.video_id} className="border border-border p-3" data-testid={`rendy-video-card-${v.video_id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.created_at).toLocaleString()}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            v.status === "ready"
                              ? "bg-emerald-100 text-emerald-700"
                              : v.status === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                          data-testid={`rendy-video-status-${v.video_id}`}
                        >
                          {(v.status === "generating" || v.status === "exporting") && (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          )}
                          {STATUS_LABEL[v.status] || v.status}
                          {v.status === "generating" && v.progress != null ? ` \u00b7 ${v.progress}%` : ""}
                        </span>
                        <button
                          type="button"
                          onClick={() => deleteVideo(v.video_id)}
                          disabled={deletingId === v.video_id}
                          title="Delete this generation from the Rendy video library"
                          className="p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                          data-testid={`rendy-delete-video-btn-${v.video_id}`}
                        >
                          {deletingId === v.video_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                    {v.status === "failed" && v.error && (
                      <p className="text-xs text-red-600 flex items-center gap-1" data-testid={`rendy-video-error-${v.video_id}`}>
                        <X className="h-3 w-3" /> {v.error}
                      </p>
                    )}
                    {v.status === "failed" && v.listing_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none mt-2"
                        onClick={() => retryDownload(v.video_id)}
                        disabled={retryingId === v.video_id}
                        data-testid={`rendy-retry-download-btn-${v.video_id}`}
                      >
                        {retryingId === v.video_id ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        )}
                        Retry download
                      </Button>
                    )}
                    {v.status === "ready" && v.videos?.length > 0 && (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`rendy-video-clips-${v.video_id}`}>
                          {v.videos.map((clip, i) => {
                            const toggleKey = `${v.video_id}-${i}`;
                            return (
                              <div key={clip.filename || i} className="space-y-1">
                                <video
                                  src={`${API.replace(/\/api$/, "")}${clip.url}`}
                                  poster={clip.poster ? `${API.replace(/\/api$/, "")}${clip.poster}` : undefined}
                                  controls
                                  className="w-full rounded border border-border bg-black"
                                  data-testid={`rendy-video-player-${v.video_id}-${i}`}
                                />
                                <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground px-0.5">
                                  <span>Clip {i + 1} · {clip.in_gallery ? "In gallery" : "Not in gallery"}</span>
                                  <Switch
                                    checked={!!clip.in_gallery}
                                    disabled={togglingClip === toggleKey}
                                    onCheckedChange={(checked) => toggleClipInGallery(v.video_id, i, checked)}
                                    aria-label={`Include clip ${i + 1} in gallery`}
                                    data-testid={`rendy-clip-gallery-switch-${v.video_id}-${i}`}
                                  />
                                </label>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            onClick={() => addToGallery(v.video_id)}
                            disabled={addingId === v.video_id}
                            variant="outline"
                            size="sm"
                            className="rounded-none text-xs"
                            data-testid={`rendy-add-to-gallery-btn-${v.video_id}`}
                          >
                            {addingId === v.video_id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <ImagePlus className="h-3 w-3 mr-1" />
                            )}
                            Add to Property Gallery
                          </Button>
                          <Button
                            onClick={() => removeFromGallery(v.video_id)}
                            disabled={removingId === v.video_id}
                            variant="outline"
                            size="sm"
                            className="rounded-none text-xs text-red-600 hover:text-red-700"
                            data-testid={`rendy-remove-from-gallery-btn-${v.video_id}`}
                          >
                            {removingId === v.video_id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3 mr-1" />
                            )}
                            Remove from Gallery
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
